/* Shreya theme — core runtime. No third-party dependencies. */
(() => {
  const Theme = (window.Theme = window.Theme || {});
  Theme.routes = Theme.routes || {};
  Theme.settings = Theme.settings || {};
  Theme.strings = Theme.strings || {};

  /* ---------------- Utilities ---------------- */
  Theme.debounce = (fn, wait = 250) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn.apply(null, args), wait); };
  };

  Theme.throttle = (fn) => {
    let queued = false;
    return (...args) => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; fn.apply(null, args); });
    };
  };

  Theme.qs = (sel, root = document) => root.querySelector(sel);
  Theme.qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  Theme.parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  /** Fetch one or more sections via the Section Rendering API. Returns { id: html }. */
  Theme.fetchSections = async (ids, url = window.location.pathname) => {
    const list = Array.isArray(ids) ? ids : [ids];
    const sep = url.includes('?') ? '&' : '?';
    const res = await fetch(`${url}${sep}sections=${list.join(',')}`);
    if (!res.ok) throw new Error('Section fetch failed');
    return res.json();
  };

  /** Fetch a single section's HTML (string) for any url. */
  Theme.fetchSection = async (id, url = window.location.pathname) => {
    const sep = url.includes('?') ? '&' : '?';
    const res = await fetch(`${url}${sep}section_id=${id}`);
    if (!res.ok) throw new Error('Section fetch failed');
    return res.text();
  };

  /** Replace innerHTML of a target with the same selector inside new html. */
  Theme.swapContent = (html, selector, root = document) => {
    const doc = typeof html === 'string' ? Theme.parseHTML(html) : html;
    const fresh = doc.querySelector(selector);
    const current = root.querySelector(selector);
    if (fresh && current) current.innerHTML = fresh.innerHTML;
    return current;
  };

  /* Money formatting using the shop's money format. */
  Theme.formatMoney = (cents, format) => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const fmt = format || (Theme.settings.showCurrencyCode ? Theme.settings.moneyWithCurrencyFormat : Theme.settings.moneyFormat) || '{{amount}}';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const withDelimiters = (n, precision, thousands = ',', decimal = '.') => {
      if (isNaN(n) || n == null) return '0';
      const fixed = (n / 100).toFixed(precision);
      const [whole, frac] = fixed.split('.');
      const wholeFmt = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return frac ? wholeFmt + decimal + frac : wholeFmt;
    };
    const match = fmt.match(placeholder);
    let value = '';
    switch (match ? match[1] : 'amount') {
      case 'amount': value = withDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' ', ''); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
      default: value = withDelimiters(cents, 2);
    }
    return fmt.replace(placeholder, value);
  };

  /* ---------------- Pub/sub ---------------- */
  const listeners = {};
  Theme.on = (event, cb) => {
    (listeners[event] = listeners[event] || []).push(cb);
    return () => { listeners[event] = listeners[event].filter((f) => f !== cb); };
  };
  Theme.emit = (event, data) => {
    (listeners[event] || []).forEach((cb) => cb(data));
    document.dispatchEvent(new CustomEvent(`theme:${event}`, { detail: data }));
  };

  /* ---------------- Toast ---------------- */
  Theme.toast = (message, ms = 2600) => {
    const el = document.querySelector('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(el._t);
    el._t = setTimeout(() => { el.hidden = true; }, ms);
  };

  /* ---------------- Scroll lock & overlay ---------------- */
  let lockCount = 0;
  const overlay = () => document.querySelector('[data-page-overlay]');
  Theme.lockScroll = () => {
    lockCount++;
    document.body.classList.add('is-locked');
    const o = overlay();
    if (o) o.hidden = false;
  };
  Theme.unlockScroll = () => {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) {
      document.body.classList.remove('is-locked');
      const o = overlay();
      if (o) o.hidden = true;
    }
  };

  /* ---------------- Focus trap ---------------- */
  const FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), iframe';
  Theme.trapFocus = (container) => {
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      const items = Theme.qsa(FOCUSABLE, container).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    container.addEventListener('keydown', handler);
    const first = Theme.qsa(FOCUSABLE, container).find((el) => el.offsetParent !== null);
    if (first) setTimeout(() => first.focus({ preventScroll: true }), 50);
    return () => container.removeEventListener('keydown', handler);
  };

  /* ---------------- Cart API ---------------- */
  const cartSections = () => {
    const ids = ['cart-drawer'];
    Theme.qsa('[data-cart-section]').forEach((el) => ids.push(el.dataset.cartSection));
    return Array.from(new Set(ids)).filter((id) => document.getElementById(`shopify-section-${id}`) || id === 'cart-drawer');
  };

  const jsonPost = async (url, body) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || Theme.strings.error);
    return data;
  };

  Theme.cart = {
    async get() {
      const res = await fetch(`${Theme.routes.cart}.js`);
      return res.json();
    },
    /** items: [{id, quantity, properties?, selling_plan?}] or a FormData */
    async add(items) {
      const sections = cartSections();
      let data;
      if (items instanceof FormData) {
        items.append('sections', sections.join(','));
        items.append('sections_url', window.location.pathname);
        const res = await fetch(`${Theme.routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: items });
        data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || Theme.strings.error);
      } else {
        data = await jsonPost(`${Theme.routes.cartAdd}.js`, { items, sections, sections_url: window.location.pathname });
      }
      const cart = await Theme.cart.get();
      Theme.emit('cart:updated', { cart, sections: data.sections, source: 'add', item: data });
      return { cart, data };
    },
    async change(line, quantity) {
      const data = await jsonPost(`${Theme.routes.cartChange}.js`, { line, quantity, sections: cartSections(), sections_url: window.location.pathname });
      Theme.emit('cart:updated', { cart: data, sections: data.sections, source: 'change' });
      return data;
    },
    async update(updates, extra = {}) {
      const data = await jsonPost(`${Theme.routes.cartUpdate}.js`, { updates, ...extra, sections: cartSections(), sections_url: window.location.pathname });
      Theme.emit('cart:updated', { cart: data, sections: data.sections, source: 'update' });
      return data;
    },
    async note(note) {
      return jsonPost(`${Theme.routes.cartUpdate}.js`, { note });
    },
  };

  /* Keep every cart counter in sync. */
  Theme.on('cart:updated', ({ cart }) => {
    if (!cart) return;
    Theme.qsa('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.dataset.count = cart.item_count;
    });
  });

  /* After adding: open drawer / go to cart / toast according to settings */
  Theme.on('cart:updated', ({ source }) => {
    if (source !== 'add') return;
    const type = Theme.settings.cartType;
    if (type === 'page') { window.location.href = Theme.routes.cart; return; }
    if (type === 'drawer' && document.getElementById('cart-drawer')) {
      Theme.openDrawer('cart-drawer');
      return;
    }
    Theme.toast(Theme.strings.added);
  });

  /* ---------------- Drawer ---------------- */
  /*
    Markup: <drawer-panel id="search-drawer"><div class="drawer" ...>...</div></drawer-panel>
    Open with any element having data-drawer-open="search-drawer". Close with [data-drawer-close].
  */
  const SHEET_MQ = window.matchMedia('(max-width: 749px)');
  class DrawerPanel extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.drawer, .modal');
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]') || e.target.classList.contains('modal__backdrop')) {
          e.preventDefault();
          this.close();
        }
      });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
      /* Esc must still work when focus has left the panel (e.g. its content was re-rendered). */
      this.onDocKeydown = (e) => { if (e.key === 'Escape' && this.isOpen && !this.contains(e.target)) this.close(); };
      document.addEventListener('keydown', this.onDocKeydown);
      if (this.panel && this.panel.classList.contains('drawer') && !this.sheetReady) {
        this.sheetReady = true;
        this.enableSheetDrag();
      }
    }
    /* Phones show drawers as bottom sheets (components.css): pull down on the grip/header,
       or on the content once it is scrolled to the top, to dismiss. */
    get isSheet() {
      return !!this.panel && SHEET_MQ.matches && !this.panel.matches('.drawer--side-mobile, .drawer--top');
    }
    addGrip() {
      if (!this.panel || this.panel.querySelector(':scope > .drawer__grip')) return;
      const grip = document.createElement('div');
      grip.className = 'drawer__grip';
      grip.setAttribute('aria-hidden', 'true');
      this.panel.prepend(grip);
    }
    enableSheetDrag() {
      const panel = this.panel;
      this.addGrip();
      let g = null;
      const scrollerFor = (el) => {
        for (let n = el; n && n !== panel; n = n.parentElement) {
          if (n.scrollHeight > n.clientHeight + 1 && /(auto|scroll)/.test(getComputedStyle(n).overflowY)) return n;
        }
        return null;
      };
      panel.addEventListener('touchstart', (e) => {
        g = null;
        if (!this.isOpen || !this.isSheet || e.touches.length !== 1) return;
        const target = e.target;
        if (target.closest('input, textarea, select, [contenteditable], [data-no-sheet-drag]')) return;
        const t = e.touches[0];
        g = { x: t.clientX, y: t.clientY, trail: [], dy: 0, active: false,
          handle: !!target.closest('.drawer__grip, .drawer__header'), scroller: scrollerFor(target) };
      }, { passive: true });
      panel.addEventListener('touchmove', (e) => {
        if (!g) return;
        const t = e.touches[0];
        const d = t.clientY - g.y;
        if (!g.active) {
          const dx = Math.abs(t.clientX - g.x);
          if (Math.abs(d) < 6 && dx < 6) return;
          /* upward or sideways swipes, and pulls on scrolled content, stay native */
          if (d <= 0 || dx > Math.abs(d) || (!g.handle && g.scroller && g.scroller.scrollTop > 0)) { g = null; return; }
          g.active = true;
          g.y = t.clientY;
          panel.classList.add('is-dragging');
        }
        if (e.cancelable) e.preventDefault();
        /* keep ~120ms of positions to measure the release speed */
        g.trail.push([e.timeStamp, t.clientY]);
        while (g.trail.length > 2 && e.timeStamp - g.trail[0][0] > 120) g.trail.shift();
        g.dy = Math.max(0, t.clientY - g.y);
        panel.style.setProperty('--sheet-drag', `${g.dy}px`);
      }, { passive: false });
      const end = () => {
        const s = g;
        g = null;
        if (!s || !s.active) return;
        panel.classList.remove('is-dragging');
        const first = s.trail[0];
        const last = s.trail[s.trail.length - 1];
        const speed = first && last && last[0] > first[0] ? (last[1] - first[1]) / (last[0] - first[0]) : 0;
        const dismiss = s.dy > 80 || (speed > 0.45 && s.dy > 24);
        if (dismiss) this.close();
        /* clearing the offset lets the sheet glide back (or onwards, when closing) */
        panel.style.removeProperty('--sheet-drag');
      };
      panel.addEventListener('touchend', end);
      panel.addEventListener('touchcancel', end);
    }
    disconnectedCallback() {
      if (this.onDocKeydown) document.removeEventListener('keydown', this.onDocKeydown);
    }
    get isOpen() { return this.panel && this.panel.classList.contains('is-open'); }
    open(trigger) {
      if (!this.panel || this.isOpen) return;
      Theme.qsa('drawer-panel').forEach((d) => d !== this && d.isOpen && d.close(true));
      this.trigger = trigger || document.activeElement;
      if (this.sheetReady) this.addGrip();
      this.panel.classList.add('is-open');
      this.panel.setAttribute('aria-hidden', 'false');
      Theme.lockScroll();
      this.releaseFocus = Theme.trapFocus(this.panel);
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
      Theme.emit('drawer:open', { id: this.id });
    }
    close(silent) {
      if (!this.isOpen) return;
      this.panel.classList.remove('is-open');
      this.panel.setAttribute('aria-hidden', 'true');
      Theme.unlockScroll();
      if (this.releaseFocus) this.releaseFocus();
      if (!silent && this.trigger && this.trigger.focus) this.trigger.focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
      Theme.emit('drawer:close', { id: this.id });
    }
  }
  if (!customElements.get('drawer-panel')) customElements.define('drawer-panel', DrawerPanel);

  Theme.openDrawer = (id, trigger) => {
    const d = document.getElementById(id);
    if (d && d.open) d.open(trigger);
  };
  Theme.closeDrawer = (id) => {
    const d = document.getElementById(id);
    if (d && d.close) d.close();
  };

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      const id = opener.dataset.drawerOpen;
      if (document.getElementById(id)) {
        e.preventDefault();
        Theme.openDrawer(id, opener);
      }
    }
  });
  document.addEventListener('click', (e) => {
    if (e.target.matches('[data-page-overlay]')) {
      Theme.qsa('drawer-panel').forEach((d) => d.isOpen && d.close());
    }
  });

  /* ---------------- Slider ---------------- */
  /*
    <slider-carousel data-autoplay="5" data-loop>
      <ul class="slider" data-slider-track>...<li>slide</li>...</ul>
      <button data-slider-prev>…</button><button data-slider-next>…</button>
      <div class="slider-dots" data-slider-dots></div> <div class="slider-progress" data-slider-progress><span></span></div>
    </slider-carousel>
    Controls may live anywhere inside the section if the element has data-controls-scope="#selector".
  */
  class SliderCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      if (!this.track) return;
      const scope = this.dataset.controlsScope ? this.closest(this.dataset.controlsScope) || this : this;
      this.prevBtns = Theme.qsa('[data-slider-prev]', scope);
      this.nextBtns = Theme.qsa('[data-slider-next]', scope);
      this.dots = scope.querySelector('[data-slider-dots]');
      this.progress = scope.querySelector('[data-slider-progress]');
      this.counter = scope.querySelector('[data-slider-counter]');
      this.loop = this.hasAttribute('data-loop');
      this.prevBtns.forEach((b) => b.addEventListener('click', () => this.go(-1)));
      this.nextBtns.forEach((b) => b.addEventListener('click', () => this.go(1)));
      this.onScroll = Theme.throttle(() => this.update());
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.resizeObs = new ResizeObserver(Theme.debounce(() => { this.buildDots(); this.update(); }, 100));
      this.resizeObs.observe(this.track);
      this.enableDrag();
      this.buildDots();
      this.update();
      const autoplay = parseFloat(this.dataset.autoplay || '0');
      if (autoplay > 0) this.startAutoplay(autoplay * 1000);
    }
    disconnectedCallback() {
      if (this.resizeObs) this.resizeObs.disconnect();
      clearInterval(this.timer);
    }
    get slides() { return Array.from(this.track.children).filter((el) => el.offsetParent !== null); }
    get step() {
      const s = this.slides;
      if (s.length < 2) return this.track.clientWidth;
      return s[1].offsetLeft - s[0].offsetLeft;
    }
    get perView() { return Math.max(1, Math.round(this.track.clientWidth / this.step)); }
    get index() { return Math.round(this.track.scrollLeft / this.step); }
    get maxScroll() { return this.track.scrollWidth - this.track.clientWidth; }
    go(dir) {
      const atEnd = this.track.scrollLeft >= this.maxScroll - 4;
      const atStart = this.track.scrollLeft <= 4;
      if (this.loop && dir > 0 && atEnd) return this.scrollToIndex(0);
      if (this.loop && dir < 0 && atStart) return this.track.scrollTo({ left: this.maxScroll });
      const by = this.dataset.stepPage !== undefined ? this.step * this.perView : this.step;
      this.track.scrollBy({ left: dir * by });
    }
    scrollToIndex(i) {
      const s = this.slides[i];
      if (s) this.track.scrollTo({ left: s.offsetLeft - this.slides[0].offsetLeft });
    }
    buildDots() {
      if (!this.dots) return;
      const pages = Math.max(1, this.slides.length - this.perView + 1);
      if (this.dots.childElementCount === pages) return;
      this.dots.innerHTML = '';
      for (let i = 0; i < pages; i++) {
        const b = document.createElement('button');
        b.type = 'button';
        b.setAttribute('aria-label', `${i + 1}`);
        b.addEventListener('click', () => this.scrollToIndex(i));
        this.dots.appendChild(b);
      }
      this.dots.hidden = pages < 2;
    }
    update() {
      const left = this.track.scrollLeft;
      const max = this.maxScroll;
      if (!this.loop) {
        this.prevBtns.forEach((b) => (b.disabled = left <= 4));
        this.nextBtns.forEach((b) => (b.disabled = left >= max - 4));
      }
      const idx = this.index;
      if (this.dots) Array.from(this.dots.children).forEach((d, i) => d.setAttribute('aria-current', i === idx));
      if (this.progress) {
        const visible = this.track.clientWidth / this.track.scrollWidth;
        const pct = max > 0 ? visible + (1 - visible) * (left / max) : 1;
        this.progress.style.setProperty('--progress', `${Math.min(100, pct * 100)}%`);
      }
      if (this.counter) this.counter.textContent = `${String(idx + 1).padStart(2, '0')} / ${String(this.slides.length).padStart(2, '0')}`;
      this.slides.forEach((s, i) => s.classList.toggle('is-active', i === idx));
      if (this._lastIdx !== idx) {
        this._lastIdx = idx;
        this.dispatchEvent(new CustomEvent('slider:change', { detail: { index: idx }, bubbles: true }));
      }
    }
    startAutoplay(ms) {
      const tick = () => { if (!document.hidden && !this.matches(':hover')) this.go(1); };
      this.loop = true;
      this.timer = setInterval(tick, ms);
    }
    enableDrag() {
      if (window.matchMedia('(pointer: coarse)').matches) return;
      let startX = 0, startLeft = 0, down = false, moved = false;
      this.track.addEventListener('pointerdown', (e) => {
        if (e.button !== 0 || e.target.closest('input, select, textarea, button')) return;
        down = true; moved = false; startX = e.clientX; startLeft = this.track.scrollLeft;
      });
      window.addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - startX;
        if (!moved && Math.abs(dx) > 6) { moved = true; this.track.classList.add('is-dragging'); }
        if (moved) this.track.scrollLeft = startLeft - dx;
      });
      window.addEventListener('pointerup', () => {
        if (!down) return;
        down = false;
        if (moved) {
          this.track.classList.remove('is-dragging');
          const i = Math.round(this.track.scrollLeft / this.step);
          this.scrollToIndex(Math.max(0, Math.min(i, this.slides.length - 1)));
        }
      });
    }
  }
  if (!customElements.get('slider-carousel')) customElements.define('slider-carousel', SliderCarousel);

  /* ---------------- Quantity input ---------------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button[name]').forEach((btn) =>
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const prev = this.input.value;
          btn.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
          if (prev !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
        })
      );
    }
  }
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  /* ---------------- Tabs ---------------- */
  /* <tabs-component><div role="tablist"><button role="tab" aria-controls="p1">…</button></div><div role="tabpanel" id="p1">…</div></tabs-component> */
  class TabsComponent extends HTMLElement {
    connectedCallback() {
      this.tabs = Theme.qsa('[role="tab"]', this);
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') { e.preventDefault(); this.select((i + 1) % this.tabs.length, true); }
          if (e.key === 'ArrowLeft') { e.preventDefault(); this.select((i - 1 + this.tabs.length) % this.tabs.length, true); }
        });
      });
    }
    select(i, focus) {
      this.tabs.forEach((tab, j) => {
        const on = i === j;
        tab.setAttribute('aria-selected', on);
        tab.tabIndex = on ? 0 : -1;
        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
      if (focus) this.tabs[i].focus();
      this.dispatchEvent(new CustomEvent('tabs:change', { detail: { index: i }, bubbles: true }));
    }
  }
  if (!customElements.get('tabs-component')) customElements.define('tabs-component', TabsComponent);

  /* ---------------- Reveal on scroll ---------------- */
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const INTERACTIVE = 'a, button, input, select, textarea, [tabindex]';

  /* Slide-up headings: wrap every word in a mask (.hw) holding the moving text (.hw__in).
     Inline elements (<em>, <a>, <strong>, <br>…) are rebuilt around their words so styling survives.
     Screen readers get one clean copy of the text instead of a run of word fragments. */
  const splitWords = (el) => {
    let index = 0;
    const build = (src, dest) => {
      Array.from(src.childNodes).forEach((node) => {
        if (node.nodeType === 3) {
          node.textContent.split(/(\s+)/).forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) { dest.appendChild(document.createTextNode(part)); return; }
            const mask = document.createElement('span');
            mask.className = 'hw';
            mask.style.setProperty('--w', index++);
            const inner = document.createElement('span');
            inner.className = 'hw__in';
            inner.textContent = part;
            mask.appendChild(inner);
            dest.appendChild(mask);
          });
        } else if (node.nodeType === 1) {
          if (node.tagName === 'BR' || node.tagName === 'IMG' || node.tagName.toLowerCase() === 'svg' || node.tagName === 'SCRIPT') {
            dest.appendChild(node.cloneNode(true));
            return;
          }
          const shell = node.cloneNode(false);
          build(node, shell);
          dest.appendChild(shell);
        }
      });
    };
    const text = el.textContent.replace(/\s+/g, ' ').trim();
    const frag = document.createDocumentFragment();
    build(el, frag);
    if (text && !el.querySelector(INTERACTIVE)) {
      const sr = document.createElement('span');
      sr.className = 'visually-hidden';
      sr.textContent = text;
      const visual = document.createElement('span');
      visual.setAttribute('aria-hidden', 'true');
      visual.appendChild(frag);
      el.replaceChildren(sr, visual);
    } else {
      el.replaceChildren(frag);
    }
  };

  /* Give each word its line number so whole lines rise together, one after another. */
  const measureLines = (el) => {
    let line = -1;
    let lastMid = -Infinity;
    let inLine = 0;
    Theme.qsa('.hw', el).forEach((word) => {
      const r = word.getBoundingClientRect();
      const mid = r.top + r.height / 2;
      if (mid - lastMid > r.height * 0.5) { line++; lastMid = mid; inLine = 0; }
      word.style.setProperty('--line', Math.max(0, line));
      word.style.setProperty('--w', inLine++);
    });
  };
  const pendingHeadings = new Set();
  window.addEventListener('resize', Theme.debounce(() => {
    pendingHeadings.forEach((el) => (el.isConnected && !el.classList.contains('is-revealed') ? measureLines(el) : pendingHeadings.delete(el)));
  }, 150));

  const splitHeading = (el) => {
    if (el.dataset.split === 'done') return;
    const mode = Theme.settings.headingAnimation;
    if (mode === 'slide-up') {
      splitWords(el);
      measureLines(el);
      pendingHeadings.add(el);
    } else if (mode === 'words') {
      let i = 0;
      const walk = (node) => {
        Array.from(node.childNodes).forEach((child) => {
          if (child.nodeType === 3) {
            const frag = document.createDocumentFragment();
            child.textContent.split(/(\s+)/).forEach((part) => {
              if (!part) return;
              if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
              const s = document.createElement('span');
              s.className = 'word';
              s.style.setProperty('--i', i++);
              s.textContent = part;
              frag.appendChild(s);
            });
            child.replaceWith(frag);
          } else if (child.nodeType === 1) walk(child);
        });
      };
      walk(el);
    }
    el.dataset.split = 'done';
  };

  const revealObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          if (pendingHeadings.has(el)) { measureLines(el); pendingHeadings.delete(el); }
          el.classList.add('is-revealed');
          revealObserver.unobserve(el);
        });
      }, { rootMargin: '0px 0px 4% 0px', threshold: 0 })
    : null;

  /* Media reveal: the image settles from a slight zoom while fading in, once it has loaded. */
  const MEDIA_BOX = '.media, .product-card__media, [data-media-reveal]';
  const REVEAL_SEL = '[data-reveal], [data-heading-reveal], [data-card-reveal]';
  const mediaTargets = (box) => Array.from(box.children).filter((c) =>
    ((c.tagName === 'IMG' && !c.classList.contains('product-card__img--hover')) || (c.classList && c.classList.contains('placeholder') && c.tagName.toLowerCase() === 'svg'))
    && c.getClientRects().length);
  const playMedia = (box) => {
    const targets = mediaTargets(box);
    const run = () => {
      if (box.classList.contains('is-media-in')) return;
      box.classList.add('is-media-in');
      targets.forEach((t) => {
        if (!t.animate) return;
        const to = getComputedStyle(t).opacity;
        t.animate([{ opacity: 0 }, { opacity: to }], { duration: 650, easing: 'cubic-bezier(.33,1,.68,1)' });
        t.animate([{ scale: '1.06' }, { scale: '1' }], { duration: 1100, easing: 'cubic-bezier(.16,1,.3,1)' });
      });
    };
    const waiting = targets.filter((t) => t.tagName === 'IMG' && !t.complete);
    if (!waiting.length) { run(); return; }
    let left = waiting.length;
    const done = () => { left -= 1; if (left <= 0) run(); };
    waiting.forEach((t) => { t.addEventListener('load', done, { once: true }); t.addEventListener('error', done, { once: true }); });
    setTimeout(run, 900);
  };
  const mediaObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          mediaObserver.unobserve(entry.target);
          playMedia(entry.target);
        });
      }, { rootMargin: '0px 0px 12% 0px', threshold: 0 })
    : null;

  const SLIDER_TRACK = '[data-slider-track]';
  const revealTrack = (track) => {
    track.querySelectorAll(REVEAL_SEL).forEach((el) => {
      if (el.classList.contains('is-revealed')) return;
      if (pendingHeadings.has(el)) { measureLines(el); pendingHeadings.delete(el); }
      el.classList.add('is-revealed');
    });
    track.querySelectorAll(MEDIA_BOX).forEach((box) => { if (!box.classList.contains('is-media-in')) playMedia(box); });
  };
  const sliderObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          revealTrack(entry.target);
          // keep watching: slides added later (recommendations, quick add) are revealed on the next pass
        });
      }, { rootMargin: '0px 0px 4% 0px', threshold: 0 })
    : null;

  Theme.initReveal = (root = document) => {
    const animate = document.body.classList.contains('has-animations') && revealObserver && !reducedMotion();
    const withRoot = (sel) => {
      const list = Theme.qsa(sel, root);
      if (root !== document && root.matches && root.matches(sel)) list.unshift(root);
      return list;
    };
    const all = withRoot(REVEAL_SEL);
    const media = withRoot(MEDIA_BOX);
    if (!animate) {
      all.forEach((el) => el.classList.add('is-revealed'));
      withRoot('[data-heading-reveal]').forEach((el) => { el.dataset.split = el.dataset.split || 'skip'; });
      media.forEach((el) => el.classList.add('is-media-in'));
      return;
    }
    withRoot('[data-heading-reveal]').forEach(splitHeading);
    // Slider items are revealed as one unit when the carousel enters the viewport, so slides that
    // start off-screen never animate in while being swiped (no cards rising/settling mid-swipe).
    const tracks = new Set();
    const inTrack = (el) => {
      const t = el.closest(SLIDER_TRACK);
      if (t) tracks.add(t);
      return !!t;
    };
    // stagger cards within each parent (capped so later slides don't lag behind)
    const groups = new Map();
    withRoot('[data-card-reveal]').forEach((el) => {
      if (!Theme.settings.cardAnimation) { el.classList.add('is-revealed'); return; }
      const p = el.closest(SLIDER_TRACK) || el.parentElement;
      const n = groups.get(p) || 0;
      el.style.setProperty('--reveal-index', Math.min(n, 5));
      groups.set(p, n + 1);
    });
    all.forEach((el) => !el.classList.contains('is-revealed') && !inTrack(el) && revealObserver.observe(el));
    media.forEach((el) => !el.classList.contains('is-media-in') && !inTrack(el) && mediaObserver.observe(el));
    tracks.forEach((t) => sliderObserver.observe(t));
  };

  /* Content injected later (quick view, recommendations, cart, predictive search…) is picked up automatically. */
  const watchInserted = () => {
    if (!('MutationObserver' in window)) return;
    const sel = `${REVEAL_SEL}, ${MEDIA_BOX}`;
    new MutationObserver((records) => {
      const roots = new Set();
      records.forEach((rec) => rec.addedNodes.forEach((n) => {
        if (n.nodeType !== 1 || n.classList.contains('hw') || n.closest('.hw')) return;
        if (n.matches(sel) || n.querySelector(sel)) roots.add(n);
      }));
      roots.forEach((n) => n.isConnected && Theme.initReveal(n));
    }).observe(document.body, { childList: true, subtree: true });
  };

  /* ---------------- Product form (add to cart) ---------------- */
  /* <product-form><form action="/cart/add" ...> with input[name=id]; submit button [type=submit]; error [data-form-error] */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    async onSubmit(e) {
      e.preventDefault();
      const btn = e.submitter && e.submitter.type === 'submit' ? e.submitter : this.form.querySelector('[type="submit"]');
      if (!btn || btn.disabled) return;
      const error = this.querySelector('[data-form-error]');
      if (error) error.hidden = true;
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
      try {
        const fd = new FormData(this.form);
        await Theme.cart.add(fd);
        this.dispatchEvent(new CustomEvent('product:added', { bubbles: true }));
        const qv = this.closest('drawer-panel#quick-view-drawer');
        if (qv && Theme.settings.cartType === 'drawer') qv.close(true);
      } catch (err) {
        if (error) { error.textContent = err.message; error.hidden = false; }
        else Theme.toast(err.message);
      } finally {
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-busy');
      }
    }
  }
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

  /* Simple "add this variant" buttons on cards: <button data-quick-add="variantId"> */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    btn.classList.add('is-loading');
    try { await Theme.cart.add([{ id: Number(btn.dataset.quickAdd), quantity: 1 }]); }
    catch (err) { Theme.toast(err.message); }
    finally { btn.classList.remove('is-loading'); }
  });

  /* ---------------- Variant picker ---------------- */
  /*
    <variant-picker data-section="{{ section.id }}" data-url="{{ product.url }}" data-update-url>
      inputs/selects with name="options[Color]" (radio) or select[data-option-index]
      <script type="application/json" data-variants>[...product.variants...]</script>
    </variant-picker>
    The nearest [data-product-root] receives updates:
      [data-variant-id] input value, [data-price] innerHTML, [data-add-button] state,
      [data-sku], [data-inventory], and media with [data-media-id].
  */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('script[data-variants]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.root = this.closest('[data-product-root]') || document;
      this.addEventListener('change', () => this.onChange());
      this.updateAvailability();
    }
    get selectedOptions() {
      const groups = Theme.qsa('[data-option-group]', this);
      return groups.map((g) => {
        const sel = g.querySelector('select');
        if (sel) return sel.value;
        const checked = g.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }
    onChange() {
      const opts = this.selectedOptions;
      const variant = this.variants.find((v) => v.options.every((o, i) => o === opts[i]));
      Theme.qsa('[data-option-group]', this).forEach((g, i) => {
        const label = g.querySelector('[data-option-selected]');
        if (label) label.textContent = opts[i] || '';
      });
      this.updateAvailability();
      this.applyVariant(variant);
      this.dispatchEvent(new CustomEvent('variant:change', { detail: { variant }, bubbles: true }));
      Theme.emit('variant:change', { variant, root: this.root });
    }
    updateAvailability() {
      const opts = this.selectedOptions;
      Theme.qsa('[data-option-group]', this).forEach((g, i) => {
        Theme.qsa('input[type="radio"], option', g).forEach((input) => {
          const value = input.value;
          const match = this.variants.some((v) => v.available && v.options[i] === value && v.options.every((o, j) => j === i || j > i || o === opts[j]));
          const label = input.tagName === 'OPTION' ? input : input.nextElementSibling;
          if (label) label.classList.toggle('is-unavailable', !match);
        });
      });
    }
    applyVariant(variant) {
      const root = this.root;
      const btn = root.querySelector('[data-add-button]');
      const btnText = btn && btn.querySelector('[data-add-text]');
      if (!variant) {
        if (btn) btn.disabled = true;
        if (btnText) btnText.textContent = Theme.strings.unavailable;
        return;
      }
      Theme.qsa('[data-variant-id]', root).forEach((input) => { input.value = variant.id; input.dispatchEvent(new Event('change', { bubbles: true })); });
      const priceEl = root.querySelector('[data-price]');
      if (priceEl) {
        const compare = variant.compare_at_price && variant.compare_at_price > variant.price;
        let html = `<span class="price__current">${Theme.formatMoney(variant.price)}</span>`;
        if (compare) {
          html = `<span class="price__current">${Theme.formatMoney(variant.price)}</span><s class="price__compare">${Theme.formatMoney(variant.compare_at_price)}</s>`;
          const pct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          if (pct > 0) html += `<span class="price__save">-${pct}%</span>`;
        }
        priceEl.innerHTML = html;
      }
      if (btn) {
        btn.disabled = !variant.available;
        if (btnText) btnText.textContent = variant.available ? (btn.dataset.preorder ? Theme.strings.preorder : Theme.strings.addToCart) : Theme.strings.soldOut;
      }
      const sku = root.querySelector('[data-sku]');
      if (sku) sku.textContent = variant.sku || '';
      const inv = root.querySelector('[data-inventory]');
      if (inv) {
        const qty = variant.inventory_quantity;
        const threshold = parseInt(inv.dataset.threshold || '5', 10);
        inv.classList.toggle('is-low', variant.available && variant.inventory_management && qty > 0 && qty <= threshold);
        if (!variant.available) inv.textContent = Theme.strings.soldOut;
        else if (variant.inventory_management && qty > 0 && qty <= threshold) inv.textContent = Theme.strings.lowStock.replace('[count]', qty);
        else inv.textContent = Theme.strings.inStock;
      }
      if (variant.featured_media) {
        Theme.emit('media:select', { root, mediaId: variant.featured_media.id });
      }
      if (this.hasAttribute('data-update-url') && this.dataset.url) {
        try { window.history.replaceState({}, '', `${this.dataset.url}?variant=${variant.id}`); } catch (_) { /* ignore */ }
      }
    }
  }
  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

  /* Card swatches: hovering/clicking swaps the card image */
  document.addEventListener('click', (e) => {
    const sw = e.target.closest('[data-card-swatch]');
    if (!sw) return;
    e.preventDefault();
    const card = sw.closest('.product-card');
    const img = card && card.querySelector('.product-card__img--main');
    if (img && sw.dataset.image) {
      img.srcset = '';
      img.src = sw.dataset.image;
    }
    if (card && sw.dataset.url) {
      Theme.qsa('a[data-card-link]', card).forEach((a) => (a.href = sw.dataset.url));
    }
    card && Theme.qsa('[data-card-swatch]', card).forEach((s) => s.classList.toggle('is-active', s === sw));
  });

  /* ---------------- Share / copy link ---------------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-copy-link]');
    if (!btn) return;
    e.preventDefault();
    const url = btn.dataset.copyLink || window.location.href;
    try {
      if (navigator.share && btn.hasAttribute('data-native-share')) await navigator.share({ url, title: document.title });
      else { await navigator.clipboard.writeText(url); Theme.toast(Theme.strings.copied); }
    } catch (_) { /* cancelled */ }
  });

  /* ---------------- Magnetic buttons ---------------- */
  /*
    Round controls lean toward the pointer and ease back when it leaves. Each element eases (lerp) toward
    a target offset on every animation frame; its icon travels a little further than the shell for depth.
    Strength defaults to 0.3 and can be set per element with data-magnetic="0.2".
  */
  const MAGNETIC = '[data-magnetic], .slider-btn, .site-header__icon, quantity-input button, .back-to-top, .social-icons a, .site-footer__social a';
  const magState = new Map();
  let magFrame = 0;
  const magTick = () => {
    magFrame = 0;
    magState.forEach((st, el) => {
      st.x += (st.tx - st.x) * 0.16;
      st.y += (st.ty - st.y) * 0.16;
      const settled = Math.abs(st.tx - st.x) < 0.05 && Math.abs(st.ty - st.y) < 0.05;
      if (settled && st.tx === 0 && st.ty === 0) {
        el.style.translate = '';
        if (st.inner) st.inner.style.translate = '';
        magState.delete(el);
        return;
      }
      el.style.translate = `${st.x.toFixed(2)}px ${st.y.toFixed(2)}px`;
      if (st.inner) st.inner.style.translate = `${(st.x * 0.45).toFixed(2)}px ${(st.y * 0.45).toFixed(2)}px`;
    });
    if (magState.size) magFrame = requestAnimationFrame(magTick);
  };
  const magKick = () => { if (!magFrame) magFrame = requestAnimationFrame(magTick); };
  const magEnabled = () => Theme.settings.magnetic && window.matchMedia('(hover: hover) and (pointer: fine)').matches && !reducedMotion();
  const initMagnetic = () => {
    if (initMagnetic.done) return;
    initMagnetic.done = true;
    let current = null;
    const release = (el) => {
      const st = magState.get(el);
      if (st) { st.tx = 0; st.ty = 0; magKick(); }
    };
    document.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse' || !magEnabled()) return;
      const el = e.target.closest ? e.target.closest(MAGNETIC) : null;
      if (current && current !== el) { release(current); current = null; }
      if (!el || el.disabled) return;
      current = el;
      let st = magState.get(el);
      if (!st) {
        st = { x: 0, y: 0, tx: 0, ty: 0, inner: el.querySelector('[data-magnetic-inner], svg') };
        magState.set(el, st);
      }
      const r = el.getBoundingClientRect();
      const strength = parseFloat(el.dataset.magnetic) || (el.closest('quantity-input') ? 0.2 : 0.3);
      // measure from the resting centre, not the already shifted box
      st.tx = (e.clientX - (r.left - st.x + r.width / 2)) * strength;
      st.ty = (e.clientY - (r.top - st.y + r.height / 2)) * strength;
      magKick();
    }, { passive: true });
    document.addEventListener('pointerout', (e) => {
      if (current && !current.contains(e.relatedTarget)) { release(current); current = null; }
    }, { passive: true });
  };

  /* ---------------- Button hover geometry ---------------- */
  /*
    .btn hover fills grow from --btn-ox/--btn-oy. Icon buttons grow from the icon ring and their arrow glides
    by --btn-travel to the far end; other buttons grow from where the pointer entered and shrink toward
    where it left.
  */
  const prepareButton = (btn, e) => {
    const r = btn.getBoundingClientRect();
    const cs = getComputedStyle(btn);
    const bl = parseFloat(cs.borderLeftWidth) || 0;
    const icon = btn.querySelector(':scope > .btn__icon');
    if (icon) {
      const ir = icon.getBoundingClientRect();
      const cx = ir.left + ir.width / 2;
      const end = r.right - (parseFloat(cs.borderRightWidth) || 0) - (parseFloat(cs.paddingLeft) || 0) - ir.width / 2;
      btn.style.setProperty('--btn-travel', `${(end - cx).toFixed(1)}px`);
      btn.style.setProperty('--btn-ox', `${(cx - r.left - bl).toFixed(1)}px`);
    } else if (e && typeof e.clientX === 'number' && e.pointerType === 'mouse') {
      btn.style.setProperty('--btn-ox', `${Math.round(e.clientX - r.left)}px`);
      btn.style.setProperty('--btn-oy', `${Math.round(e.clientY - r.top)}px`);
    }
  };
  document.addEventListener('pointerover', (e) => {
    const btn = e.target.closest ? e.target.closest('.btn') : null;
    if (btn && !btn.contains(e.relatedTarget)) prepareButton(btn, e);
  }, { passive: true });
  document.addEventListener('pointerout', (e) => {
    const btn = e.target.closest ? e.target.closest('.btn') : null;
    if (!btn || btn.contains(e.relatedTarget) || btn.querySelector(':scope > .btn__icon') || e.pointerType !== 'mouse') return;
    const r = btn.getBoundingClientRect();
    btn.style.setProperty('--btn-ox', `${Math.round(e.clientX - r.left)}px`);
    btn.style.setProperty('--btn-oy', `${Math.round(e.clientY - r.top)}px`);
  }, { passive: true });
  document.addEventListener('focusin', (e) => {
    const btn = e.target.closest ? e.target.closest('.btn') : null;
    if (btn) prepareButton(btn);
  });

  /* ---------------- Back to top ---------------- */
  const initBackToTop = () => {
    const btn = document.querySelector('[data-back-to-top]');
    if (!btn) return;
    btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
    window.addEventListener('scroll', Theme.throttle(() => { btn.hidden = window.scrollY < 600; }), { passive: true });
  };

  /* ---------------- Accordion: optional single-open groups ---------------- */
  document.addEventListener('toggle', (e) => {
    const d = e.target;
    if (!(d instanceof HTMLDetailsElement) || !d.open) return;
    const group = d.closest('[data-accordion-single]');
    if (!group) return;
    Theme.qsa('details[open]', group).forEach((other) => other !== d && (other.open = false));
  }, true);

  /* ---------------- Boot ---------------- */
  Theme.init = (root = document) => {
    Theme.initReveal(root);
    initMagnetic();
  };

  const boot = () => {
    Theme.init(document);
    document.documentElement.classList.add('reveal-ready');
    watchInserted();
    initBackToTop();
    document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* Theme editor support */
  document.addEventListener('shopify:section:load', (e) => Theme.init(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    const slide = e.target.closest('[data-slider-track] > *');
    const slider = e.target.closest('slider-carousel');
    if (slide && slider) slider.scrollToIndex(Array.from(slide.parentElement.children).indexOf(slide));
  });
})();
